// Prepares a photo in the browser before upload: decodes it (any format the browser can show,
// including camera photos), applies its orientation, scales it down and re-encodes it as JPEG. The
// re-encoded file carries no metadata (no location). The server checks it again regardless.
const MAX_EDGE = 2048;
const QUALITY = 0.85;

export class PhotoPrepareError extends Error {
  override readonly name = 'PhotoPrepareError';
}

export async function preparePhoto(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new PhotoPrepareError('unsupported');
  }
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (context === null) throw new PhotoPrepareError('canvas');
    context.drawImage(bitmap, 0, 0, width, height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob === null) reject(new PhotoPrepareError('encode'));
          else resolve(blob);
        },
        'image/jpeg',
        QUALITY,
      );
    });
  } finally {
    bitmap.close();
  }
}

/** The authorized address of a stored photo (served by the API per request, never public). */
export const photoUrl = (projectId: string, handoverId: string, photoId: string): string =>
  `/api/projects/${projectId}/handovers/${handoverId}/photos/${photoId}`;
